import React, { useEffect, useState } from 'react'
import { AppServices } from 'middleware/services'
import { useHistory, useParams } from 'react-router-dom'
import { getActiveAccount } from 'preferences/selectors'
import { useAppSelector } from 'store/hooks'
import { Permission, AccountUser, AccountUserRequest, UserRole, UserPin } from 'settings/schema'
import { AI_PERMISSIONS, getDependentPermissions, getPrerequisitePermissions, userFormValidationSchema } from './schema'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { FormikProvider, useFormik } from 'formik'
import classNames from 'classnames'
import { LoaderWrapper } from 'uiComponents/loaders'
import { ResellerResponseItem } from 'settings/settingsService'
import { PersonalInformationSection } from './sections/personalInformation'
import { ActionsSection } from './sections/actionsSection'
import { useHasFeature } from 'features'
import { ControlPanelPermissionGroup } from './permissionGroups/controlPanel'
import { PointOfSalePermissionGroup } from './permissionGroups/pointOfSale'
import { ConviousAiPermissionGroup } from './permissionGroups/conviousAi'
import { GeneralPermissionGroup } from './permissionGroups/general'
import './userForm.scss'
import { usePulseAccess } from 'pulse/hooks/usePulseAccess'
import { useTranslation } from 'translations'

const fromBackendToFormValues = (user: AccountUser, posUserManagementEnabled: boolean, pulseBetaEnabled: boolean) => {
    const isAdmin = user.role === 'admin' || user.role === 'staff'
    const permissionsObject: Record<Permission, boolean> = {
        partner_admin: isAdmin,
        access_products: isAdmin,
        view_pricing_settings: isAdmin,
        edit_pricing_settings: isAdmin,
        view_product_validity: isAdmin,
        edit_product_validity: isAdmin,
        access_orders: isAdmin,
        edit_orders: isAdmin,
        create_order: isAdmin,
        export_orders: isAdmin,
        refund_orders: isAdmin,
        read_carts: isAdmin,
        write_carts: isAdmin,
        control_panel_login: isAdmin,
        access_analytics: isAdmin,
        access_codes: isAdmin,
        view_wonderbar: isAdmin,
        edit_wonderbar: isAdmin,
        pos_login: isAdmin && posUserManagementEnabled,
        // The AI claims are stored explicitly even for admins (the token needs
        // them verbatim), so the toggles reflect the returned permissions only.
        access_pulse: false,
        access_pulse_sales: false,
        access_ai_assistant: false,
        mcp: false,
    }
    for (const permission of user.permissions) {
        permissionsObject[permission as Permission] = true
    }
    return {
        ...user,
        permissions: permissionsObject,
    }
}

const fromFormValuesToBackend = (values: any, accountSlug: string) => {
    const valuesCopy = { ...values, permissions: { ...values.permissions } }
    let grantedPermissions = new Set(
        Object.keys(valuesCopy.permissions).filter((permission) => valuesCopy.permissions[permission]),
    )
    let role = valuesCopy.role

    if (grantedPermissions.has('access_products')) {
        grantedPermissions.add('access_categories')
    } else {
        grantedPermissions.delete('access_categories')
    }

    if (grantedPermissions.has('pos_login')) {
        grantedPermissions.add('get_pricing')
    } else {
        grantedPermissions.delete('get_pricing')
    }

    if (valuesCopy.conviousAdmin) {
        grantedPermissions.clear()
    } else if (valuesCopy.permissions.partner_admin) {
        grantedPermissions = new Set([
            'partner_admin',
            ...AI_PERMISSIONS.filter((permission) => valuesCopy.permissions[permission]),
        ])
    }

    return {
        ...valuesCopy,
        role: role,
        accounts: [accountSlug],
        permissions: [
            {
                account: accountSlug,
                permissions: Array.from(grantedPermissions),
            },
        ],
    }
}

const emptyUser = {
    email: '',
    firstName: '',
    lastName: '',
    permissions: {
        partner_admin: false,
        access_products: false,
        view_pricing_settings: false,
        edit_pricing_settings: false,
        view_product_validity: false,
        edit_product_validity: false,
        access_orders: false,
        edit_orders: false,
        create_order: false,
        export_orders: false,
        refund_orders: false,
        read_carts: false,
        write_carts: false,
        control_panel_login: false,
        access_analytics: false,
        access_codes: false,
        view_wonderbar: false,
        edit_wonderbar: false,
        pos_login: false,
        access_pulse: false,
        access_pulse_sales: false,
        access_ai_assistant: false,
        mcp: false,
    },
}

export const UserForm = () => {
    const { t } = useTranslation()
    const accountSettingsService = AppServices.accountSettingsService
    const history = useHistory()
    const accountSlug = useAppSelector(getActiveAccount)
    const { id: userId } = useParams<{ id: string }>()
    const isNewUser = userId === 'create'
    const [loading, setLoading] = useState(true)
    const [initialValues, setInitialValues] = useState<any>(emptyUser)
    const [loadingResellers, setLoadingResellers] = useState(true)
    const [resellersList, setResellersList] = useState<ResellerResponseItem[]>([])
    const [emailChecked, setEmailChecked] = useState(!isNewUser)
    const [resellerFieldDisabled, setResellerFieldDisabled] = useState(!isNewUser)
    const userRole =
        (initialValues as AccountUser).role ?? (new URLSearchParams(location.search).get('role') as UserRole)
    const [userPin, setUserPin] = useState<UserPin | null>(null)
    const posUserManagementEnabled = useHasFeature('pos_user_management')
    const pulseBetaEnabled = usePulseAccess()

    if (!accountSlug) {
        return null
    }

    const returnToUserList = () => {
        history.push(`/account/${accountSlug}/settings/users/user_list`)
    }

    const getUserPin = async (userId: number) => {
        await accountSettingsService
            .getUserPins(accountSlug, userId)
            .then((userPins) => {
                const newUserPin = Object.values(userPins).find((pin) => pin.user_id === userId)
                setUserPin(newUserPin ?? null)
            })
            .catch(() => {
                showErrorMessage(
                    t('settings.users.form.messages.posSettingsLoadError'),
                    'user_pos_settings_could_not_be_loaded',
                )
            })
    }

    const getUserById = async (userId: number) => {
        setLoading(true)
        const user = await accountSettingsService.getUserById(accountSlug, userId)
        if (posUserManagementEnabled) {
            await getUserPin(userId)
        }
        setInitialValues(fromBackendToFormValues(user, posUserManagementEnabled, pulseBetaEnabled))
        setLoading(false)
    }

    const loadNewUser = async (email: string) => {
        setLoading(true)
        const users = await accountSettingsService.getUserList(accountSlug, '', '')
        const newUser = users.find((user) => user.email === email)
        if (newUser?.id) {
            history.push(`/account/${accountSlug}/settings/users/user_form/${newUser.id}`)
        } else {
            returnToUserList()
        }
        setLoading(false)
    }

    const getResellersList = async () => {
        try {
            const resellers = await accountSettingsService.getResellersList(accountSlug)
            setResellersList(resellers)
        } catch {
            showErrorMessage(t('settings.users.form.messages.resellersLoadError'), 'unknown_error')
        }
    }

    const generateUserPin = async (userId: number) => {
        try {
            const response = await accountSettingsService.generateUserPin(accountSlug, userId)
            setUserPin(response)
            return response
        } catch {
            showErrorMessage(t('settings.users.form.messages.pinGenerationError'))
            return null
        }
    }

    const regenerateUserPin = async (pinId: number) => {
        try {
            const response = await accountSettingsService.regenerateUserPin(accountSlug, pinId)
            setUserPin(response)
            return response
        } catch {
            showErrorMessage(t('settings.users.form.messages.pinRegenerationError'))
            return null
        }
    }

    useEffect(() => {
        if (isNewUser) {
            const isAdmin = userRole === 'admin' || userRole === 'staff'
            setInitialValues({
                email: '',
                firstName: '',
                lastName: '',
                accounts: [accountSlug],
                role: userRole,
                permissions: {
                    partner_admin: isAdmin,
                    access_products: isAdmin || userRole === 'reseller',
                    view_pricing_settings: isAdmin,
                    edit_pricing_settings: isAdmin,
                    view_product_validity: isAdmin,
                    edit_product_validity: isAdmin,
                    access_orders: isAdmin || userRole === 'reseller',
                    edit_orders: isAdmin,
                    create_order: isAdmin,
                    export_orders: isAdmin,
                    refund_orders: isAdmin,
                    read_carts: isAdmin,
                    write_carts: isAdmin,
                    control_panel_login: true,
                    access_analytics: isAdmin,
                    access_codes: isAdmin,
                    view_wonderbar: isAdmin,
                    edit_wonderbar: isAdmin,
                    pos_login: isAdmin && posUserManagementEnabled,
                    access_pulse: pulseBetaEnabled && userRole !== 'reseller',
                    access_pulse_sales: isAdmin && pulseBetaEnabled,
                    access_ai_assistant: isAdmin && pulseBetaEnabled,
                    mcp: isAdmin && pulseBetaEnabled,
                },
                conviousAdmin: userRole === 'staff',
                resellerId: null,
            })
            setLoading(false)
        } else {
            getUserById(Number(userId))
        }
    }, [accountSlug, userId])

    useEffect(() => {
        if (userRole === 'reseller') {
            setLoadingResellers(true)
            getResellersList().finally(() => setLoadingResellers(false))
        }
    }, [userRole])

    const handlePermissionClick = (permission: Permission, granted: boolean) => {
        formik.setFieldValue(`permissions.${permission}`, granted)

        if (granted) {
            const prerequisites = getPrerequisitePermissions(permission)
            for (const prerequisite of prerequisites) {
                formik.setFieldValue(`permissions.${prerequisite}`, true)
            }
        } else {
            const dependents = getDependentPermissions(permission)
            for (const dependent of dependents) {
                formik.setFieldValue(`permissions.${dependent}`, false)
            }
        }
    }

    const handleAdminClick = (granted: boolean) => {
        formik.setFieldValue('role', granted ? 'admin' : 'user')

        const permissionsToUpdate = Object.keys(formik.values.permissions).filter(
            (permission: Permission) =>
                (posUserManagementEnabled || !permission.startsWith('pos_')) &&
                (pulseBetaEnabled || !AI_PERMISSIONS.includes(permission)),
        )

        permissionsToUpdate.forEach((permission) => {
            formik.setFieldValue(`permissions.${permission}`, granted)
        })
    }

    const updateUserData = async (userData: AccountUserRequest) => {
        setLoading(true)
        try {
            const response = await accountSettingsService.createOrUpdateUser(userData)
            if (response.ok) {
                showSuccessMessage(
                    isNewUser
                        ? t('settings.users.form.messages.userCreated', { email: userData.email })
                        : t('settings.users.form.messages.userUpdated', { email: userData.email }),
                    `${isNewUser ? 'create_user_successful' : 'update_user_successful'}`,
                )
                if (isNewUser) {
                    await loadNewUser(userData.email)
                }
            } else {
                if (response.message === 'User exists') {
                    showErrorMessage(t('settings.users.form.messages.userExists'), 'user_exists')
                } else if (response.message === 'Not authorised') {
                    showErrorMessage(t('settings.users.form.messages.notAuthorised'), 'not_authorised')
                } else {
                    showErrorMessage(t('settings.users.form.messages.unknownError'), 'unknown_error')
                }
            }
        } catch {
            showErrorMessage(t('settings.users.form.messages.unknownError'), 'unknown_error')
        }
        setLoading(false)
    }

    const deleteUser = async (email: string) => {
        try {
            const response = await accountSettingsService.createOrUpdateUser({
                email,
                deletedAccounts: [accountSlug],
                accounts: [],
                permissions: [],
                conviousAdmin: false,
                resellerId: null,
            })
            if (response.ok) {
                showSuccessMessage(t('settings.users.form.messages.userDeleted', { email }), 'delete_user_successful')
                returnToUserList()
            } else {
                showErrorMessage(t('settings.users.form.messages.deleteError'), 'unable_to_delete_user')
            }
        } catch {
            showErrorMessage(t('settings.users.form.messages.unknownError'), 'unknown_error')
        }
    }

    const checkEmailValid = async (email: string) => {
        formik.setFieldError('email', '')
        const existingUser = await accountSettingsService.getExistingUser(accountSlug, email)
        if (existingUser) {
            if (existingUser.currentAccountUser) {
                formik.setFieldError('email', t('settings.users.form.messages.userAlreadyExists'))
                return false
            } else if (existingUser.role === 'reseller' && existingUser.resellerId) {
                if (userRole !== 'reseller') {
                    formik.setFieldError('email', t('settings.users.form.messages.emailUsedByReseller'))
                    return false
                }

                if (resellersList.find((reseller) => existingUser.resellerId === reseller.resellerId)) {
                    formik.setFieldValue('resellerId', existingUser.resellerId)
                    setResellerFieldDisabled(true)
                    return true
                } else {
                    formik.setFieldError('email', t('settings.users.form.messages.emailUsedByOtherReseller'))
                    return false
                }
            }
        }
        return true
    }

    const onSubmit = async (values: any) => {
        if (isNewUser && !emailChecked) {
            return
        }
        const backendValues = fromFormValuesToBackend(values, accountSlug)
        await updateUserData(backendValues)
    }

    const formik = useFormik({
        initialValues,
        onSubmit,
        enableReinitialize: true,
        validationSchema: userFormValidationSchema,
        validateOnChange: false,
        validateOnBlur: false,
    })

    useEffect(() => {
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [formik.submitCount, formik.errors])

    const isAdminGranted = formik.values.permissions?.partner_admin
    const isControlPanelLoginGranted = formik.values.permissions?.control_panel_login
    const isPosLoginGranted = formik.values.permissions?.pos_login
    const allowSave =
        emailChecked && (!isNewUser || userRole === 'staff' || isPosLoginGranted || isControlPanelLoginGranted)

    return (
        <FormikProvider value={formik}>
            <form name="user-form" className={classNames('user-form', { loading })} onSubmit={formik.handleSubmit}>
                <LoaderWrapper loading={loading}>
                    <PersonalInformationSection
                        email={formik.values.email}
                        emailConfirmed={!isNewUser && formik.values.requiresPasswordChange === false}
                        isNewUser={isNewUser}
                        isControlPanelLoginGranted={isControlPanelLoginGranted}
                        checkEmailValid={checkEmailValid}
                        setEmailChecked={setEmailChecked}
                    />
                    <GeneralPermissionGroup
                        userRole={userRole}
                        isAdminGranted={isAdminGranted}
                        handlePermissionClick={handlePermissionClick}
                        handleAdminClick={handleAdminClick}
                    />
                    <ControlPanelPermissionGroup
                        userRole={userRole}
                        formik={formik}
                        resellerFieldDisabled={resellerFieldDisabled}
                        resellersList={resellersList}
                        loadingResellers={loadingResellers}
                        isAdminGranted={isAdminGranted}
                        handlePermissionClick={handlePermissionClick}
                    />
                    {pulseBetaEnabled && userRole !== 'reseller' && (
                        <ConviousAiPermissionGroup
                            isPulseAccessGranted={formik.values.permissions?.access_pulse}
                            handlePermissionClick={handlePermissionClick}
                        />
                    )}
                    {userRole !== 'reseller' && (
                        <PointOfSalePermissionGroup
                            userId={isNewUser ? null : Number(userId)}
                            userPin={userPin}
                            isPosLoginGranted={isPosLoginGranted}
                            posUserManagementEnabled={posUserManagementEnabled}
                            isAdminGranted={isAdminGranted}
                            generateUserPin={generateUserPin}
                            regenerateUserPin={regenerateUserPin}
                            handlePermissionClick={handlePermissionClick}
                        />
                    )}
                    <ActionsSection
                        formik={formik}
                        showDeleteButton={!isNewUser}
                        disableSaveButton={!allowSave}
                        deleteUser={deleteUser}
                        returnToUserList={returnToUserList}
                    />
                </LoaderWrapper>
            </form>
        </FormikProvider>
    )
}
